import { type AppendMessage, type ThreadMessageLike, useExternalStoreRuntime } from "@assistant-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GcsAttachmentAdapter } from "./attachments/GcsAttachmentAdapter";
import { DeepgramDictationAdapter } from "./dictation/DeepgramDictationAdapter";

// ── Internal message types ──────────────────────────────────────────────

export interface WidgetItem {
  id: string;
  type: "followup_questions" | "sources";
  data: unknown;
}

export interface AttachmentInfo {
  id: string;
  filename: string;
  contentType?: string;
  size?: number;
}

export interface InternalMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  widgets?: WidgetItem[];
  attachments?: AttachmentInfo[];
}

// ── SSE event shapes from the backend ────────────────────────────────────

interface SSEThreadCreated {
  type: "thread.created";
  thread: { id: string };
}

interface SSEItemAdded {
  type: "thread.item.added";
  item: {
    type: "user_message" | "assistant_message" | "widget";
    id: string;
    thread_id: string;
    created_at: string;
    content?: Array<{ type: string; text: string }>;
    widget?: Record<string, unknown>;
  };
}

interface SSEItemUpdated {
  type: "thread.item.updated";
  item_id: string;
  update: {
    type: string;
    content_index?: number;
    delta?: string;
    content?: { type: string; text: string };
  };
}

interface SSEItemDone {
  type: "thread.item.done";
  item: {
    type: "user_message" | "assistant_message";
    id: string;
    thread_id: string;
    created_at: string;
    content: Array<{ type: string; text: string }>;
  };
}

interface SSEError {
  type: "error";
  message: string;
}

type SSEEvent = SSEThreadCreated | SSEItemAdded | SSEItemUpdated | SSEItemDone | SSEError;

// ── Widget parsing ───────────────────────────────────────────────────────

function parseWidgetFromItem(item: SSEItemAdded["item"]): WidgetItem | null {
  if (item.type !== "widget" || !item.widget) return null;

  const widget = item.widget as Record<string, unknown>;
  const children = widget.children as Array<Record<string, unknown>> | undefined;

  // Follow-up questions widget
  if (widget.type === "Basic" && children) {
    const questions: string[] = [];
    for (const child of children) {
      if (child.type === "Card") {
        const cardChildren = child.children as Array<Record<string, unknown>> | undefined;
        if (cardChildren) {
          for (const lv of cardChildren) {
            if (lv.type === "ListView") {
              const lvChildren = lv.children as Array<Record<string, unknown>> | undefined;
              for (const lvi of lvChildren || []) {
                if (lvi.type === "ListViewItem") {
                  const action = lvi.onClickAction as Record<string, unknown> | undefined;
                  if (action?.type === "send_followup") {
                    const payload = action.payload as Record<string, unknown>;
                    if (payload?.question) questions.push(payload.question as string);
                  }
                }
              }
            }
          }
        }
      }
    }
    if (questions.length > 0) {
      return { id: item.id, type: "followup_questions", data: questions };
    }
  }

  // Sources widget
  if (widget.type === "Card") {
    const sources: Array<{ title: string; url?: string }> = [];
    const cardChildren = widget.children as Array<Record<string, unknown>> | undefined;
    for (const lv of cardChildren || []) {
      if ((lv as Record<string, unknown>).type === "ListView") {
        const lvChildren = (lv as Record<string, unknown>).children as Array<Record<string, unknown>> | undefined;
        for (const lvi of lvChildren || []) {
          const action = lvi.onClickAction as Record<string, unknown> | undefined;
          const itemChildren = lvi.children as Array<Record<string, unknown>> | undefined;
          let title = "";
          let url: string | undefined;

          if (action?.type === "open_url") {
            const payload = action.payload as Record<string, unknown>;
            url = payload?.url as string;
          }

          for (const c of itemChildren || []) {
            if (c.type === "Markdown" && c.value) {
              const md = c.value as string;
              const match = md.match(/\[([^\]]*)\]\(([^)]*)\)/);
              if (match) {
                title = match[1];
                if (!url) url = match[2];
              } else {
                title = md;
              }
            }
          }
          if (title || url) sources.push({ title, url });
        }
      }
    }
    if (sources.length > 0) {
      return { id: item.id, type: "sources", data: sources };
    }
  }

  return null;
}

// ── Thread data fetching ─────────────────────────────────────────────────

async function fetchThreadMessages(
  baseUrl: string,
  threadId: string,
  headers: Record<string, string>,
): Promise<InternalMessage[]> {
  const res = await fetch(`${baseUrl}/superdoc/`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({
      type: "threads.get_by_id",
      params: { thread_id: threadId },
    }),
  });

  if (!res.ok) return [];
  const thread = await res.json();
  const items: Array<Record<string, unknown>> = thread.items?.data || [];

  const messages: InternalMessage[] = [];
  const widgetQueue: WidgetItem[] = [];

  for (const item of items) {
    const itemType = item.type as string;
    if (itemType === "user_message" || itemType === "assistant_message") {
      if (widgetQueue.length > 0 && messages.length > 0) {
        const lastMsg = messages[messages.length - 1];
        if (lastMsg.role === "assistant") {
          lastMsg.widgets = [...(lastMsg.widgets || []), ...widgetQueue];
        }
        widgetQueue.length = 0;
      }

      const contentParts = item.content as Array<{ type: string; text: string }>;
      const text = contentParts?.map((p) => p.text).join("") || "";
      messages.push({
        id: item.id as string,
        role: itemType === "user_message" ? "user" : "assistant",
        content: text,
        createdAt: item.created_at as string,
      });
    } else if (itemType === "widget") {
      const parsed = parseWidgetFromItem(item as SSEItemAdded["item"]);
      if (parsed) widgetQueue.push(parsed);
    }
  }

  if (widgetQueue.length > 0 && messages.length > 0) {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role === "assistant") {
      lastMsg.widgets = [...(lastMsg.widgets || []), ...widgetQueue];
    }
  }

  return messages;
}

async function fetchThreadList(
  baseUrl: string,
  headers: Record<string, string>,
): Promise<Array<{ id: string; title: string }>> {
  const res = await fetch(`${baseUrl}/superdoc/`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ type: "threads.list", params: {} }),
  });
  if (!res.ok) return [];
  const data = await res.json();
  return (data.data || []).map((t: Record<string, unknown>) => ({
    id: t.id as string,
    title: t.title as string,
  }));
}

// ── SSE stream parser ────────────────────────────────────────────────────

function parseSSEEvents(text: string): SSEEvent[] {
  const events: SSEEvent[] = [];
  const lines = text.split("\n");
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("data: ")) continue;
    const jsonStr = trimmed.slice(6);
    if (!jsonStr || jsonStr === "[DONE]") continue;
    try {
      events.push(JSON.parse(jsonStr) as SSEEvent);
    } catch {
      // skip malformed
    }
  }
  return events;
}

// ── Hook ─────────────────────────────────────────────────────────────────

interface UseSuperdocRuntimeOptions {
  baseUrl: string;
  clientId?: number;
  modelId?: string;
  initialThreadId: string | null;
}

export function useSuperdocRuntime({ baseUrl, clientId, modelId, initialThreadId }: UseSuperdocRuntimeOptions) {
  const [messages, setMessages] = useState<InternalMessage[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(initialThreadId);
  const abortRef = useRef<AbortController | null>(null);

  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  const isRunningRef = useRef(isRunning);
  isRunningRef.current = isRunning;

  const getHeaders = useCallback(() => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = localStorage.getItem("token");
    if (token) headers["Authorization"] = `Token ${token}`;
    if (clientId) headers["X-Client-ID"] = clientId.toString();
    if (modelId) headers["X-Model"] = modelId;
    return headers;
  }, [clientId, modelId]);

  // Load initial thread messages
  useEffect(() => {
    if (!initialThreadId) return;
    let cancelled = false;

    fetchThreadMessages(baseUrl, initialThreadId, getHeaders()).then((msgs) => {
      if (!cancelled) {
        setMessages(msgs);
        setThreadId(initialThreadId);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, initialThreadId, getHeaders]);

  // Process a single SSE event and update state
  const handleSSEEvent = useCallback(
    (event: SSEEvent, ctx: { assistantText: string; assistantId: string | null; widgets: WidgetItem[] }) => {
      switch (event.type) {
        case "thread.created": {
          setThreadId(event.thread.id);
          break;
        }

        case "thread.item.added": {
          const { item } = event;
          if (item.type === "assistant_message") {
            ctx.assistantId = item.id;
            ctx.assistantText = "";
            setMessages((prev) => [
              ...prev,
              {
                id: item.id,
                role: "assistant",
                content: "",
                createdAt: item.created_at,
              },
            ]);
          } else if (item.type === "user_message") {
            const text = item.content?.map((p) => p.text).join("") || "";
            setMessages((prev) => {
              if (prev.some((m) => m.id === item.id)) return prev;
              return [
                ...prev,
                {
                  id: item.id,
                  role: "user" as const,
                  content: text,
                  createdAt: item.created_at,
                },
              ];
            });
          } else if (item.type === "widget") {
            const parsed = parseWidgetFromItem(item);
            if (parsed) ctx.widgets.push(parsed);
          }
          break;
        }

        case "thread.item.updated": {
          if (event.update.type === "assistant_message.content_part.text_delta" && event.update.delta) {
            ctx.assistantText += event.update.delta;
            const snap = ctx.assistantText;
            const id = event.item_id;
            setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, content: snap } : m)));
          }
          break;
        }

        case "thread.item.done": {
          const { item } = event;
          if (item.type === "assistant_message") {
            const finalText = item.content?.map((p) => p.text).join("") || "";
            setMessages((prev) => prev.map((m) => (m.id === item.id ? { ...m, content: finalText } : m)));
            ctx.assistantId = null;
            ctx.assistantText = "";
          } else if (item.type === "user_message") {
            const text = item.content?.map((p) => p.text).join("") || "";
            setMessages((prev) => {
              if (prev.some((m) => m.id === item.id)) return prev;
              const optimistic = prev.find(
                (m) => m.role === "user" && m.content === text && m.id.startsWith("optimistic_"),
              );
              if (optimistic) {
                return prev.map((m) =>
                  m.id === optimistic.id ? { ...m, id: item.id, createdAt: item.created_at } : m,
                );
              }
              return prev;
            });
          }
          break;
        }

        case "error": {
          console.error("[Superdoc] SSE error:", event.message);
          break;
        }
      }
    },
    [],
  );

  // Flush pending widgets onto last assistant message
  const flushWidgets = useCallback((widgets: WidgetItem[]) => {
    if (widgets.length === 0) return;
    setMessages((prev) => {
      const copy = [...prev];
      for (let i = copy.length - 1; i >= 0; i--) {
        if (copy[i].role === "assistant") {
          copy[i] = {
            ...copy[i],
            widgets: [...(copy[i].widgets || []), ...widgets],
          };
          break;
        }
      }
      return copy;
    });
  }, []);

  // Stream SSE response using ReadableStream (progressive) with fallback to text
  const processResponse = useCallback(
    async (response: Response) => {
      const ctx = { assistantText: "", assistantId: null as string | null, widgets: [] as WidgetItem[] };

      const contentType = response.headers.get("content-type") || "";

      // If the response is NOT event-stream (e.g. JSON error), handle it directly
      if (!contentType.includes("text/event-stream")) {
        console.warn("[Superdoc] Non-streaming response:", contentType);
        try {
          const json = await response.json();
          console.warn("[Superdoc] Response body:", json);
        } catch {
          const text = await response.text();
          console.warn("[Superdoc] Response text:", text);
        }
        return;
      }

      // Try ReadableStream first (progressive streaming)
      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith("data: ")) continue;
              const jsonStr = trimmed.slice(6);
              if (!jsonStr || jsonStr === "[DONE]") continue;
              try {
                const event = JSON.parse(jsonStr) as SSEEvent;
                handleSSEEvent(event, ctx);
              } catch {
                // skip malformed
              }
            }
          }

          // Process remaining buffer
          if (buffer.trim()) {
            for (const event of parseSSEEvents(buffer)) {
              handleSSEEvent(event, ctx);
            }
          }
        } catch (err) {
          // ReadableStream may fail (e.g. network error) - try fallback
          console.warn("[Superdoc] Stream read error, processing what we have:", err);
        }
      } else {
        // Fallback: read entire body as text and parse all events at once
        console.warn("[Superdoc] No ReadableStream, falling back to text()");
        const text = await response.text();
        for (const event of parseSSEEvents(text)) {
          handleSSEEvent(event, ctx);
        }
      }

      flushWidgets(ctx.widgets);
    },
    [handleSSEEvent, flushWidgets],
  );

  // Send a user message - this is called by the assistant-ui runtime
  const onNew = useCallback(
    async (message: AppendMessage) => {
      if (message.content.length === 0) return;

      let userText = "";
      for (const part of message.content) {
        if (part.type === "text") {
          userText += (part as { type: "text"; text: string }).text;
        }
      }
      if (!userText.trim()) return;

      // Extract attachment IDs from completed attachments
      const attachmentIds: string[] = [];
      if ("attachments" in message && Array.isArray(message.attachments)) {
        for (const att of message.attachments) {
          if (att.id && !att.id.startsWith("pending_")) {
            attachmentIds.push(att.id);
          }
        }
      }

      // Build attachment info for display
      const attachmentInfos: AttachmentInfo[] = [];
      if ("attachments" in message && Array.isArray(message.attachments)) {
        for (const att of message.attachments) {
          attachmentInfos.push({
            id: att.id,
            filename: att.name,
            contentType: att.contentType,
            size: att.file?.size,
          });
        }
      }

      console.log("[Superdoc] onNew called:", userText.substring(0, 80), "attachments:", attachmentIds.length);

      // Add optimistic user message
      const optimisticId = `optimistic_${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: optimisticId,
          role: "user",
          content: userText,
          createdAt: new Date().toISOString(),
          attachments: attachmentInfos.length > 0 ? attachmentInfos : undefined,
        },
      ]);

      setIsRunning(true);

      try {
        const currentThreadId = threadIdRef.current;
        const requestType = currentThreadId ? "threads.add_user_message" : "threads.create";

        const body = {
          type: requestType,
          params: {
            ...(currentThreadId ? { thread_id: currentThreadId } : {}),
            input: {
              content: [{ type: "input_text", text: userText }],
              attachments: attachmentIds.map((id) => ({ id })),
            },
          },
        };

        console.log("[Superdoc] Sending request:", requestType, "thread:", currentThreadId);

        const controller = new AbortController();
        abortRef.current = controller;

        const response = await fetch(`${baseUrl}/superdoc/`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        console.log(
          "[Superdoc] Response status:",
          response.status,
          "content-type:",
          response.headers.get("content-type"),
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error("[Superdoc] HTTP error:", response.status, errorText);
          return;
        }

        await processResponse(response);
        console.log("[Superdoc] Stream complete");
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("[Superdoc] Send message error:", err);
      } finally {
        setIsRunning(false);
        abortRef.current = null;
      }
    },
    [baseUrl, getHeaders, processResponse],
  );

  // Cancel current stream
  const onCancel = useCallback(async () => {
    abortRef.current?.abort();
    setIsRunning(false);
  }, []);

  // Send a custom action (clinical review, EHR status, etc.)
  const sendCustomAction = useCallback(
    async (action: { type: string; payload?: Record<string, unknown> }) => {
      const currentThreadId = threadIdRef.current;

      setIsRunning(true);
      try {
        const body = {
          type: "threads.custom_action",
          params: {
            thread_id: currentThreadId || "",
            action,
          },
        };

        const controller = new AbortController();
        abortRef.current = controller;

        const response = await fetch(`${baseUrl}/superdoc/`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          console.error("[Superdoc] Custom action HTTP error:", response.status);
          return;
        }

        await processResponse(response);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("[Superdoc] Custom action error:", err);
      } finally {
        setIsRunning(false);
        abortRef.current = null;
      }
    },
    [baseUrl, getHeaders, processResponse],
  );

  // Switch to a different thread
  const switchThread = useCallback(
    async (newThreadId: string) => {
      const msgs = await fetchThreadMessages(baseUrl, newThreadId, getHeaders());
      setMessages(msgs);
      setThreadId(newThreadId);
    },
    [baseUrl, getHeaders],
  );

  // Create new thread
  const createNewThread = useCallback(async () => {
    setMessages([]);
    setThreadId(null);
  }, []);

  // Get thread list
  const getThreadList = useCallback(() => fetchThreadList(baseUrl, getHeaders()), [baseUrl, getHeaders]);

  // Convert internal messages to assistant-ui format
  const convertMessage = useCallback(
    (msg: InternalMessage): ThreadMessageLike => ({
      role: msg.role,
      id: msg.id,
      content: [{ type: "text", text: msg.content }],
      createdAt: new Date(msg.createdAt),
    }),
    [],
  );

  const attachmentAdapter = useMemo(
    () =>
      new GcsAttachmentAdapter({
        baseUrl,
        getHeaders,
        getThreadId: () => threadIdRef.current,
      }),
    [baseUrl, getHeaders],
  );

  const dictationAdapter = useMemo(() => new DeepgramDictationAdapter({ baseUrl, getHeaders }), [baseUrl, getHeaders]);

  const runtime = useExternalStoreRuntime({
    messages,
    isRunning,
    onNew,
    onCancel,
    convertMessage,
    adapters: {
      attachments: attachmentAdapter,
      dictation: dictationAdapter,
    },
  });

  return useMemo(
    () => ({
      runtime,
      messages,
      threadId,
      isRunning,
      sendCustomAction,
      switchThread,
      createNewThread,
      getThreadList,
      isRunningRef,
    }),
    [runtime, messages, threadId, isRunning, sendCustomAction, switchThread, createNewThread, getThreadList],
  );
}
